import type { Tables } from "@/integrations/supabase/types";
import { effectiveCatalog, languageLabel, regionLabel, countryGroupLabel, accentLabel, genderLabel, pitchLabel, record } from "./voiceLabels";
import { VOICE_QUALITIES, voiceQualities } from "../../supabase/functions/_shared/voice-qualities";

export type CatalogVoice = Pick<Tables<"voices">,
  "id" | "name" | "display_name" | "provider" | "language" | "tags" | "characteristics" |
  "gender" | "is_active" | "sample_audio_url" | "provider_metadata" | "test_phrase" | "updated_at">;

// All descriptors come from the administrator-managed database catalog.

export const UNSPECIFIED = "Not specified";
export const FILTER_LABELS = {
  gender: "Gender", accent: "Accent", pitch: "Pitch", qualities: "Voice qualities", language: "Language", region: "Country",
} as const;
export type VoiceFacet = keyof typeof FILTER_LABELS;
export type VoiceFilters = Record<VoiceFacet, string[]> & { search: string; geography: string[] };
export const EMPTY_VOICE_FILTERS: VoiceFilters = { search: "", gender: [], accent: [], pitch: [], qualities: [], language: [], region: [], geography: [] };

const strings = (value: unknown): string[] => (Array.isArray(value) ? value : [value])
  .filter((item): item is string => typeof item === "string")
  .map(item => item.trim()).filter(Boolean);
const unique = (values: string[]) => [...new Map(values.map(value => [value.toLowerCase(), value])).values()];

export const catalogVoiceName = (voice: CatalogVoice) => voice.display_name?.trim() || voice.name || "Unnamed voice";

export function voiceCatalogDetails(voice: CatalogVoice) {
  const raw = voice.provider_metadata;
  const meta = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  const catalog = effectiveCatalog(meta);
  const overrides = record(meta.catalog_overrides);
  const providerVoice = typeof meta.voice === "string" ? meta.voice : "";
  const facets: Record<VoiceFacet, string[]> = {
    gender: [genderLabel(Object.hasOwn(overrides,"gender") ? overrides.gender : voice.gender ?? catalog.gender)],
    // Do not infer pitch from gender or accent from language/name.
    accent: [accentLabel(catalog.accent, catalog.region_code)],
    pitch: [pitchLabel(catalog.pitch)],
    qualities: voiceQualities(meta),
    language: [languageLabel(Object.hasOwn(catalog,"language_code") ? catalog.language_code : voice.language)],
    region: [regionLabel(catalog.region_code)],
  };
  for (const facet of Object.keys(facets) as VoiceFacet[]) {
    if (facet === "qualities") continue;
    facets[facet] = unique(facets[facet].length ? facets[facet] : [UNSPECIFIED]);
  }
  const duration = typeof meta.sample_duration_seconds === "number" && Number.isFinite(meta.sample_duration_seconds) && meta.sample_duration_seconds > 0
    ? meta.sample_duration_seconds : null;
  return { facets, duration, description: strings(catalog.description).join(" "), providerVoice };
}

export function voiceFacetOptions(voices: CatalogVoice[], facet: VoiceFacet) {
  if (facet === "qualities") return [...VOICE_QUALITIES];
  return unique(voices.flatMap(voice => voiceCatalogDetails(voice).facets[facet])).sort((a, b) => a.localeCompare(b));
}

export const geographyKey = (country: string, accent: string) => JSON.stringify([country, accent]);
export interface VoiceGeographyGroup { country: string; label: string; accents: { label: string; key: string }[] }
export function voiceGeographyOptions(voices: CatalogVoice[]): VoiceGeographyGroup[] {
  const countries = new Map<string, Set<string>>();
  for (const voice of voices) {
    const { facets } = voiceCatalogDetails(voice);
    for (const country of facets.region) {
      const accents = countries.get(country) ?? new Set<string>();
      facets.accent.forEach(accent => accents.add(accent));
      countries.set(country, accents);
    }
  }
  return [...countries].map(([country, accents]) => {
    // Group by the stored country, never infer nationality from a voice name.
    const label = countryGroupLabel(country);
    return { country, label, accents: [...accents].map(accent => ({
      label: accent.startsWith(`${label} – `) ? accent.slice(label.length + 3) : accent,
      key: geographyKey(country, accent),
    })).sort((a, b) => a.label.localeCompare(b.label)) };
  }).sort((a, b) => a.label.localeCompare(b.label) || a.country.localeCompare(b.country));
}

export function filterCatalogVoices(voices: CatalogVoice[], filters: VoiceFilters) {
  const search = filters.search.trim().toLowerCase();
  return voices.filter(voice => {
    const details = voiceCatalogDetails(voice);
    const haystack = [catalogVoiceName(voice), details.providerVoice, details.description, ...(voice.tags ?? []), ...Object.values(details.facets).flat()].join(" ").toLowerCase();
    const geographyMatches = !filters.geography.length || details.facets.region.some(country =>
      details.facets.accent.some(accent => filters.geography.includes(geographyKey(country, accent))));
    return (!search || haystack.includes(search)) && geographyMatches && (Object.keys(FILTER_LABELS) as VoiceFacet[])
      // Any checked value within a list, and all selected lists together.
      .every(facet => !filters[facet].length || details.facets[facet].some(value =>
        filters[facet].some(selected => value.toLowerCase() === selected.toLowerCase())));
  });
}
